import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useSession } from '../../../app/useSession';
import { useTeamMember } from '../../../app/useTeamMember';
import type { CompanyRow } from './companies.schema';
import { useCompanies } from './companiesQueries';
import { CompanyDrawer } from './CompanyDrawer';
import { CompanyList } from './CompanyList';
import styles from './companies.module.css';

export function CompaniesPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const { data: companies = [], isLoading, error } = useCompanies(orgId);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  // `?new=1` opens the create drawer (Ctrl+K "צור לקוח").
  const wantsNew = params.get('new') === '1';
  useEffect(() => {
    if (!wantsNew) return;
    setDrawerOpen(true);
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
  }, [wantsNew, params, setParams]);

  function openCreate() {
    setDrawerOpen(true);
  }

  // Clicking a row navigates to the customer master view, not a drawer.
  function navigateToProfile(company: CompanyRow) {
    navigate(`/customers/${company.id}`);
  }

  function closeDrawer() {
    setDrawerOpen(false);
  }

  return (
    <>
      <div className={styles.pageHead}>
        <div>
          <h1>לקוחות</h1>
          <p className={styles.pageSub}>הלקוחות שלך, ההיסטוריה והמצב הנוכחי של כל אחד.</p>
        </div>
        <button type="button" className={styles.btnPrimary} onClick={openCreate}>
          <Plus size={16} aria-hidden="true" />
          לקוח
        </button>
      </div>

      {isLoading ? <p className={styles.notice}>טוען לקוחות...</p> : null}
      {error ? <p className={styles.notice}>לא הצלחנו לטעון לקוחות · נסה שוב</p> : null}

      {!isLoading && !error && companies.length === 0 ? (
        <div className={styles.emptyCard}>
          <div className={styles.emptySmiley} aria-hidden="true">
            <svg viewBox="0 0 64 64" width="44" height="44">
              <circle cx="32" cy="32" r="28" fill="none" />
              <path
                d="M20 25c2-2 6-2 8 0M36 25c2-2 6-2 8 0M22 39c4 5 16 5 20 0"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p>אין לקוחות עדיין. הוסף את הראשון בלחיצה על «+ לקוח».</p>
          <button type="button" className={styles.btnPrimary} onClick={openCreate}>
            <Plus size={16} aria-hidden="true" />
            לקוח
          </button>
        </div>
      ) : null}

      {!isLoading && !error && companies.length > 0 ? (
        <CompanyList companies={companies} onSelect={navigateToProfile} />
      ) : null}

      <CompanyDrawer open={drawerOpen} company={null} orgId={orgId} onClose={closeDrawer} />
    </>
  );
}
